import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { SNAPSHOT_TIPO } from '../services/saldosService';
import { formatSnapshotHora } from '../services/saldosService';

const Overlay = styled.div`
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background-color: rgba(17, 24, 39, 0.8);
    display: flex; justify-content: center; align-items: center;
    z-index: 2100;
`;

const ModalContent = styled.div`
    background-color: ${props => props.theme.colors.surface};
    padding: 2rem;
    border-radius: 8px;
    width: 90%;
    max-width: 560px;
`;

const Title = styled.h3`
    font-family: ${props => props.theme.typography.titleFamily};
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
    color: ${props => props.theme.colors.textSecondary};
    margin: 0 0 1.5rem 0;
    font-size: 0.875rem;
`;

const OptionsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
`;

const Option = styled.label`
    display: flex;
    flex-direction: column;
    border: 2px solid ${props => props.selected ? props.theme.colors.primary : props.theme.colors.lightGray};
    background-color: ${props => props.selected ? 'rgba(26, 40, 62, 0.05)' : props.theme.colors.surface};
    border-radius: ${props => props.theme.borderRadius};
    padding: 1rem;
    cursor: pointer;
    transition: border-color 0.2s, background-color 0.2s;
    &:hover { border-color: ${props => props.theme.colors.primary}; }
`;

const OptionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    color: ${props => props.theme.colors.secondary};
    margin-bottom: 0.25rem;
`;

const OptionDescription = styled.span`
    font-size: 0.8rem;
    color: ${props => props.theme.colors.textSecondary};
    margin-left: 1.75rem;
`;

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
`;

const Button = styled.button`
    font-family: ${props => props.theme.typography.fontFamily};
    background-color: ${props => props.primary ? props.theme.colors.primary : props.theme.colors.lightGray};
    color: ${props => props.primary ? props.theme.colors.textPrimary : props.theme.colors.secondary};
    border: none;
    border-radius: ${props => props.theme.borderRadius};
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const InfoBox = styled.div`
    background-color: rgba(26, 40, 62, 0.05);
    border-left: 3px solid ${props => props.theme.colors.primary};
    padding: 0.75rem 1rem;
    margin-bottom: 1.25rem;
    border-radius: 4px;
    font-size: 0.8rem;
    color: ${props => props.theme.colors.secondary};
    line-height: 1.5;
`;

const UploadSnapshotModal = ({ isOpen, onClose, onConfirm, snapshotsDelDia = [] }) => {
    const hayInicial = snapshotsDelDia.some(s => s.tipo === SNAPSHOT_TIPO.INICIAL);
    const ultimoSnapshot = snapshotsDelDia[snapshotsDelDia.length - 1] || null;
    const defaultTipo = hayInicial ? SNAPSHOT_TIPO.INTRADIARIO : SNAPSHOT_TIPO.INICIAL;
    const [tipo, setTipo] = useState(defaultTipo);

    useEffect(() => {
        if (isOpen) setTipo(hayInicial ? SNAPSHOT_TIPO.INTRADIARIO : SNAPSHOT_TIPO.INICIAL);
    }, [isOpen, hayInicial]);

    if (!isOpen) return null;

    const handleConfirm = () => {
        onConfirm(tipo);
    };

    return (
        <Overlay onClick={onClose}>
            <ModalContent className="axon-modal-sheet" onClick={(e) => e.stopPropagation()}>
                <Title>Cargar snapshot de saldos</Title>
                <Subtitle>Indicá si esta carga corresponde al snapshot inicial del día o a una actualización intradiaria.</Subtitle>

                {snapshotsDelDia.length > 0 && (
                    <InfoBox>
                        Hoy ya hay {snapshotsDelDia.length} snapshot{snapshotsDelDia.length > 1 ? 's' : ''} cargado{snapshotsDelDia.length > 1 ? 's' : ''}.
                        {ultimoSnapshot && (
                            <> Último: <strong>{ultimoSnapshot.tipo}</strong> a las {formatSnapshotHora(ultimoSnapshot.uploadedAt)}.</>
                        )}
                    </InfoBox>
                )}

                <OptionsList>
                    <Option selected={tipo === SNAPSHOT_TIPO.INICIAL}>
                        <OptionHeader>
                            <input
                                type="radio"
                                name="snapshot-tipo"
                                checked={tipo === SNAPSHOT_TIPO.INICIAL}
                                onChange={() => setTipo(SNAPSHOT_TIPO.INICIAL)}
                            />
                            Snapshot inicial (mañana)
                        </OptionHeader>
                        <OptionDescription>
                            Saldo del custodio al comienzo del día, antes de operar. Solo puede haber uno por día.
                            {hayInicial && ' Ya existe — al confirmar, el inicial anterior queda como histórico y este será el nuevo activo.'}
                        </OptionDescription>
                    </Option>

                    <Option selected={tipo === SNAPSHOT_TIPO.INTRADIARIO}>
                        <OptionHeader>
                            <input
                                type="radio"
                                name="snapshot-tipo"
                                checked={tipo === SNAPSHOT_TIPO.INTRADIARIO}
                                onChange={() => setTipo(SNAPSHOT_TIPO.INTRADIARIO)}
                            />
                            Snapshot intradiario
                        </OptionHeader>
                        <OptionDescription>
                            Actualización del saldo del custodio durante el día (típicamente post-15hs para reflejar rescates T+1 del día anterior). Pueden cargarse varios.
                        </OptionDescription>
                    </Option>
                </OptionsList>

                <Actions className="axon-modal-actions">
                    <Button onClick={onClose}>Cancelar</Button>
                    <Button primary onClick={handleConfirm}>Confirmar y cargar archivo</Button>
                </Actions>
            </ModalContent>
        </Overlay>
    );
};

export default UploadSnapshotModal;
